import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Mail, Sparkles, Loader2, Send, BarChart3, Briefcase, MessageSquare, Trophy, Users, RefreshCw, Settings2, Plus, X, Save } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InsightsPage = () => {
  const [data, setData] = useState(null);
  const [narrative, setNarrative] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const [digest, cfg] = await Promise.allSettled([
        axios.get(`${API_URL}/api/insights/digest`, { withCredentials: true }),
        axios.get(`${API_URL}/api/insights/config`, { withCredentials: true }),
      ]);
      if (digest.status === 'fulfilled') {
        setData(digest.value.data?.data);
        setNarrative(digest.value.data?.narrative || '');
      }
      if (cfg.status === 'fulfilled') setConfig(cfg.value.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load digest'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPreview(); }, []);

  const handleSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/insights/send-digest`, {}, { withCredentials: true });
      toast.success(res.data?.message || 'Digest sent');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const res = await axios.put(`${API_URL}/api/insights/config`, config, { withCredentials: true });
      setConfig(res.data);
      toast.success('Schedule & recipients saved — scheduler updated');
      setConfigOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSavingConfig(false); }
  };

  const handleAddRecipient = () => {
    const email = newRecipient.trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error('Invalid email'); return; }
    if ((config?.extra_recipients || []).includes(email)) { toast.error('Already added'); return; }
    setConfig({ ...config, extra_recipients: [...(config?.extra_recipients || []), email] });
    setNewRecipient('');
  };

  const removeRecipient = (email) => {
    setConfig({ ...config, extra_recipients: (config?.extra_recipients || []).filter(r => r !== email) });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!data) return <p className="text-center text-muted-foreground py-12">No data</p>;

  const Section = ({ icon: Icon, title, children }) => (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Icon className="w-4 h-4" /> {title}</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-1">{children}</CardContent>
    </Card>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="insights-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Mail className="w-7 h-7 text-purple-500" /> Weekly Insights Digest</h1>
          <p className="text-muted-foreground text-sm">Sent automatically every Monday 8:30 AM IST. Period: <span className="font-semibold">{data.period.from} – {data.period.to}</span></p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchPreview} className="gap-2" data-testid="refresh-digest-btn"><RefreshCw className="w-4 h-4" /> Refresh</Button>
          <Button variant="outline" onClick={() => setConfigOpen(true)} className="gap-2" data-testid="config-digest-btn"><Settings2 className="w-4 h-4" /> Configure</Button>
          <Button onClick={() => setConfirmOpen(true)} disabled={sending} className="gap-2 bg-purple-600 hover:bg-purple-700 text-white" data-testid="send-digest-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Now
          </Button>
        </div>
      </div>

      {narrative && (
        <Card className="border-purple-200 dark:border-purple-900/40 bg-gradient-to-br from-purple-50/40 to-transparent dark:from-purple-900/10">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-purple-700 dark:text-purple-400 mb-1">AI Insight</p>
                <p className="text-sm whitespace-pre-wrap" data-testid="ai-narrative">{narrative}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Section icon={BarChart3} title="Performance">
          <p>📝 <b>{data.performance.review_count}</b> new reviews</p>
          <p>📊 Avg score: <b>{data.performance.avg_score}</b></p>
          {Object.keys(data.performance.rating_breakdown || {}).length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1">{Object.entries(data.performance.rating_breakdown).map(([k, v]) => <Badge key={k} variant="outline" className="text-xs capitalize">{k}: {v}</Badge>)}</div>
          )}
        </Section>

        <Section icon={Briefcase} title="Hiring">
          <p>🆕 <b>{data.hiring.new_requisitions}</b> new requisitions</p>
          <p>⚡ <b>{data.hiring.auto_triggered}</b> auto-triggered (exits)</p>
          <p>📋 <b>{data.hiring.open_total}</b> open total</p>
        </Section>

        <Section icon={MessageSquare} title="1:1 Meetings">
          <p>✅ <b>{data.meetings.completed}</b> completed</p>
          <p>⏳ <b>{data.meetings.pending_action_items}</b> action items in flight</p>
        </Section>

        <Section icon={Trophy} title="Engagement">
          <p>🎉 <b>{data.engagement.witties_earned}</b> Witties earned</p>
          {data.engagement.top_wordle_solver && <p>🏆 Top Wordle: <b>{data.engagement.top_wordle_solver}</b> ({data.engagement.top_wordle_streak}-day streak)</p>}
          {data.engagement.active_pulse_survey && <p className="italic text-muted-foreground text-xs pt-1">Pulse: "{data.engagement.active_pulse_survey}"</p>}
        </Section>

        <Section icon={Users} title="Operations">
          <p>👥 <b>{data.operations.active_employees}</b> active employees</p>
          <p>🌴 <b>{data.operations.pending_leaves}</b> pending leaves</p>
          <p>🧾 <b>{data.operations.pending_expenses}</b> pending expenses</p>
        </Section>
      </div>

      <Card className="bg-muted/30">
        <CardContent className="p-4 text-xs text-muted-foreground flex items-start gap-2">
          <Mail className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p>
              {config?.enabled !== false ? (
                <>Auto-sent every <b className="text-foreground">{(config?.day_of_week || 'mon').toUpperCase()}</b> at <b className="text-foreground">{String(config?.hour || 8).padStart(2, '0')}:{String(config?.minute || 30).padStart(2, '0')} {config?.timezone || 'Asia/Kolkata'}</b>.</>
              ) : <span className="text-amber-600 dark:text-amber-400">Auto-send disabled.</span>}
              {' '}Click "Configure" to change schedule or recipients. "Send Now" forces an immediate send.
            </p>
            {config?.extra_recipients?.length > 0 && (
              <p className="mt-1">Extra recipients: {config.extra_recipients.join(', ')}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Confirm Send Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-send-dialog">
          <DialogHeader>
            <DialogTitle>Send Weekly Digest Now?</DialogTitle>
            <DialogDescription>This will email the digest immediately to all configured recipients via Brevo SMTP. The scheduled send will still happen as usual.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="cancel-send-btn">Cancel</Button>
            <Button onClick={handleSend} className="gap-2 bg-purple-600 hover:bg-purple-700 text-white" data-testid="confirm-send-btn">
              <Send className="w-4 h-4" /> Yes, Send Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="config-dialog">
          <DialogHeader>
            <DialogTitle>Digest Schedule & Recipients</DialogTitle>
            <DialogDescription>Configure when and to whom the weekly insights digest is sent automatically.</DialogDescription>
          </DialogHeader>
          {config && (
            <div className="space-y-5">
              <div className="flex items-center justify-between p-3 rounded border">
                <div>
                  <p className="text-sm font-semibold">Auto-send</p>
                  <p className="text-xs text-muted-foreground">Master toggle for the scheduled send</p>
                </div>
                <Switch checked={config.enabled !== false} onCheckedChange={(v) => setConfig({ ...config, enabled: v })} data-testid="enabled-toggle" />
              </div>

              <div className="space-y-3">
                <p className="text-sm font-semibold">Schedule</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Day of week</Label>
                    <Select value={config.day_of_week || 'mon'} onValueChange={(v) => setConfig({ ...config, day_of_week: v })}>
                      <SelectTrigger data-testid="day-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'], ['thu', 'Thursday'], ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday']].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Hour (0-23)</Label>
                    <Input type="number" min="0" max="23" value={config.hour ?? 8} onChange={(e) => setConfig({ ...config, hour: parseInt(e.target.value) || 0 })} data-testid="hour-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Minute (0-59)</Label>
                    <Input type="number" min="0" max="59" value={config.minute ?? 30} onChange={(e) => setConfig({ ...config, minute: parseInt(e.target.value) || 0 })} data-testid="minute-input" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Timezone: {config.timezone || 'Asia/Kolkata'}</p>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-semibold flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-purple-600" /> AI Narrative Tone</p>
                <Select value={config.tone || 'professional'} onValueChange={(v) => setConfig({ ...config, tone: v })}>
                  <SelectTrigger data-testid="tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional — neutral, balanced</SelectItem>
                    <SelectItem value="founder_direct">Founder-Direct — punchy, candid, no fluff</SelectItem>
                    <SelectItem value="warm_empathetic">Warm & Empathetic — celebrates wins, gentle on the load</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Changes the voice of the AI Insight paragraph at the top of the digest email.</p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Recipients</p>
                  <div className="flex items-center gap-2 text-xs">
                    <Switch checked={config.include_admins !== false} onCheckedChange={(v) => setConfig({ ...config, include_admins: v })} data-testid="include-admins-toggle" />
                    <span className="text-muted-foreground">Include all admins</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input type="email" placeholder="founder@wisewit.ai" value={newRecipient} onChange={(e) => setNewRecipient(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddRecipient()} data-testid="new-recipient-input" />
                  <Button variant="outline" onClick={handleAddRecipient} className="gap-1" data-testid="add-recipient-btn"><Plus className="w-3 h-3" /> Add</Button>
                </div>
                <div className="flex flex-wrap gap-1.5 min-h-[32px]">
                  {(config.extra_recipients || []).length > 0 ? (config.extra_recipients || []).map(r => (
                    <Badge key={r} variant="secondary" className="gap-1 text-xs" data-testid={`recipient-${r}`}>
                      {r}
                      <button onClick={() => removeRecipient(r)} className="hover:text-destructive ml-1"><X className="w-3 h-3" /></button>
                    </Badge>
                  )) : <p className="text-xs text-muted-foreground italic">No extra recipients beyond admins</p>}
                </div>
              </div>
              <div className="space-y-3 pt-3 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold">Attach last-week dataset (CSV)</p>
                    <p className="text-xs text-muted-foreground">Adds a CSV file with the full digest data to every email.</p>
                  </div>
                  <Switch checked={!!config.attach_csv} onCheckedChange={(v) => setConfig({ ...config, attach_csv: v })} data-testid="attach-csv-toggle" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveConfig} disabled={savingConfig} className="gap-2 bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-config-btn">
              {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InsightsPage;
